import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import { usePassTypes } from "../hooks/usePassTypes";
import { usePassTypeMutation } from "../hooks/usePassTypeMutation";
import type { PassType } from "../models/PassType";
import { PASS_TYPE_INITIAL_VALUES } from "../models/PassType";
import { PassTypesGrid } from "./PassTypesGrid";
import { PassTypeDetailViewer } from "./PassTypeDetailViewer";
import { PassTypesToolbar } from "./PassTypesToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

// Default layout percentages
const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	passTypeId: urlParam.int(),
};

export const PassTypesManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, passTypeId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [passTypeToDelete, setPassTypeToDelete] = useState<PassType | null>(
		null,
	);

	// Layout persistence
	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "pass-types-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	// Data fetching
	const {
		passTypes: allPassTypes,
		isLoading,
		error: queryError,
	} = usePassTypes();

	// Client-side search filtering
	const passTypes = useMemo(() => {
		const base = onlyActives
			? allPassTypes.filter((p) => p.Active)
			: allPassTypes;
		if (!searchText || searchText.trim() === "") {
			return base;
		}
		const searchLower = searchText.toLowerCase();
		return base.filter(
			(p) =>
				p.Name?.toLowerCase().includes(searchLower) ||
				p.Description?.toLowerCase().includes(searchLower),
		);
	}, [allPassTypes, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<PassType>({
		id: passTypeId,
		setId: (value) => setFilters({ passTypeId: value }),
		dropId: () => dropValues("passTypeId"),
		rows: allPassTypes,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedPassType = selection.current;

	// Mutations
	const { savePassType, deletePassType, isSaving, isDeleting } =
		usePassTypeMutation({
			onSuccess: (passType) => {
				const isNew = selection.isNew;
				setNotification(
					isNew
						? "Pass type created successfully"
						: "Pass type updated successfully",
				);
				if (passType) {
					selection.adopt(passType);
				}
			},
			onError: (err) => setError(err.message),
			onDeleteSuccess: () => {
				setNotification("Pass type deleted successfully");
				selection.clear();
				setDeleteModalOpen(false);
				setPassTypeToDelete(null);
			},
		});

	// Handlers
	const handleRowSelected = useCallback(
		(passType: PassType | null) => {
			if (passType) selection.select(passType);
			else selection.clear();
		},
		[selection],
	);

	const handleAddNew = useCallback(() => {
		selection.startNew({ ...PASS_TYPE_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(passType: PassType) => {
			savePassType(passType);
		},
		[savePassType],
	);

	const handleDelete = useCallback((passType: PassType) => {
		setPassTypeToDelete(passType);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (passTypeToDelete?.Id) {
			deletePassType(passTypeToDelete.Id);
		}
	}, [passTypeToDelete, deletePassType]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setPassTypeToDelete(null);
	}, []);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Pass Types
			</Typography>

			<Paper elevation={3}>
				<PassTypesToolbar
					onReset={() => resetValues(["search", "onlyActives"])}
					canReset={!isDefault("search", "onlyActives")}
					search={searchText}
					onSearchChange={setSearchText}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAddNew}
				/>
			</Paper>

			<Group
				id="pass-types-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{
								flex: 1,
								width: "100%",
								overflow: "hidden",
							}}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<PassTypesGrid
									passTypes={passTypes}
									selectedPassType={
										selectedPassType?.Id ? selectedPassType : null
									}
									onSelectPassType={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`grip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<PassTypeDetailViewer
								passType={selectedPassType}
								existingPassTypes={allPassTypes}
								isSaving={isSaving}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE PASS TYPE"
				itemName={passTypeToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
